import { useEffect, useState, type ReactNode, useCallback, useRef } from 'react';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { authFetch, buildApiUrl, setAuthTokenProvider } from '@/services/api/client';
import { AuthContextProvider, type User } from './AuthContextBase';

export { AuthContext, useAuth } from './AuthContextBase';

const ME_REQUEST_TIMEOUT_MS = 8000;
const CLERK_DEV_BOOT_TIMEOUT_MS = 5000;
import { ALL_PERMISSIONS } from '@/lib/permissions';

const LOCAL_AUTH_BYPASS = import.meta.env.DEV && import.meta.env.VITE_LOCAL_AUTH_BYPASS === 'true';

const DEV_BYPASS_USER: User = {
  uid: 'local-dev-user',
  email: 'local-dev@canopy.test',
  displayName: 'Local Dev',
  propertyIds: ['soundry', 'harmony', 'vinyl', 'terra_lodge', 'monroe'],
  preferredProperty: 'soundry',
  // Must be a real Role enum value — 'admin' is not one, and require_role()
  // scores anything outside the hierarchy as viewer, so local dev silently
  // lost every admin-gated affordance.
  role: 'super_admin',
  // Local dev bypass never calls /me, so spell out the super_admin grant that
  // the server would have returned. ALL_PERMISSIONS keeps every nav item
  // visible; without it the default-deny gate hides the whole sidebar in dev.
  permissions: [...ALL_PERMISSIONS],
  organizationId: null,
  organizationRole: null,
  tenantId: null,
  tenantRole: null,
  tenantPropertyIds: [],
  tenantPortfolioAccess: false,
};

function BypassAuthProvider({ children }: { children: ReactNode }) {
  const checkAuth = useCallback(async () => true, []);
  const logout = useCallback(async () => undefined, []);
  const login = useCallback(async (_email: string, _password: string) => undefined, []);

  useEffect(() => {
    setAuthTokenProvider(null);
    return () => {
      setAuthTokenProvider(null);
    };
  }, []);

  return (
    <AuthContextProvider
      value={{
        isAuthenticated: true,
        isLoading: false,
        user: DEV_BYPASS_USER,
        login,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContextProvider>
  );
}
export function AuthProvider({ children }: { children: ReactNode }) {
  if (LOCAL_AUTH_BYPASS) {
    return <BypassAuthProvider>{children}</BypassAuthProvider>;
  }

  return <ClerkAuthProvider>{children}</ClerkAuthProvider>;
}
function ClerkAuthProviderInner({
  children,
  isLoaded,
  isSignedIn,
  getToken,
  signOut,
}: {
  children: ReactNode;
  isLoaded: boolean;
  isSignedIn: boolean | undefined;
  getToken: (options?: { skipCache?: boolean }) => Promise<string | null>;
  signOut: () => Promise<unknown>;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const inFlightProfileFetchRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn) {
      setAuthTokenProvider((options) =>
        getToken(options?.forceRefresh ? { skipCache: true } : undefined),
      );
    } else {
      setAuthTokenProvider(null);
    }
    return () => {
      setAuthTokenProvider(null);
    };
  }, [getToken, isLoaded, isSignedIn]);

  useEffect(() => {
    if (!import.meta.env.DEV || isLoaded) return;
    const timeoutId = window.setTimeout(() => {
      if (isLoaded) return;
      console.warn(
        '[auth] Clerk did not initialize within 5s in local dev. Continuing unauthenticated.',
      );
      setIsAuthenticated(false);
      setUser(null);
      setIsLoading(false);
    }, CLERK_DEV_BOOT_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isLoaded]);

  const fetchUserProfile = useCallback(async () => {
    if (inFlightProfileFetchRef.current) {
      await inFlightProfileFetchRef.current;
      return;
    }

    const fetchPromise = (async () => {
      try {
        const token = isSignedIn ? await getToken().catch(() => null) : null;
        const res = await authFetch(buildApiUrl('/me'), {
          timeout: ME_REQUEST_TIMEOUT_MS,
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!res.ok) {
          setUser(null);
          return;
        }

        const data = await res.json();
        setUser({
          uid: data.uid,
          email: data.email,
          displayName: data.display_name ?? null,
          propertyIds: data.property_ids ?? [],
          preferredProperty: data.preferred_property ?? null,
          role: data.role,
          permissions: Array.isArray(data.permissions) ? data.permissions : [],
          organizationId: data.organization_id ?? null,
          organizationRole: data.organization_role ?? null,
          tenantId: data.tenant_id ?? null,
          tenantRole: data.tenant_role ?? null,
          tenantPropertyIds: data.tenant_property_ids ?? [],
          tenantPortfolioAccess: Boolean(data.tenant_portfolio_access),
        });
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        setUser(null);
      }
    })();

    inFlightProfileFetchRef.current = fetchPromise;
    try {
      await fetchPromise;
    } finally {
      inFlightProfileFetchRef.current = null;
    }
  }, [getToken, isSignedIn]);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    if (!isLoaded) {
      setIsLoading(true);
      return false;
    }
    const authenticated = Boolean(isSignedIn);
    setIsAuthenticated(authenticated);
    if (authenticated) {
      setIsLoading(true);
      try {
        await fetchUserProfile();
      } finally {
        setIsLoading(false);
      }
    } else {
      setUser(null);
      setIsLoading(false);
    }
    return authenticated;
  }, [fetchUserProfile, isLoaded, isSignedIn]);

  const login = async (_email: string, _password: string) => {
    throw new Error('Login is handled by Clerk UI');
  };

  const logout = async () => {
    await signOut().catch(() => undefined);
    setIsAuthenticated(false);
    setUser(null);
  };

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  return (
    <AuthContextProvider value={{ isAuthenticated, isLoading, user, login, logout, checkAuth }}>
      {children}
    </AuthContextProvider>
  );
}

function ClerkAuthProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useClerkAuth();

  return (
    <ClerkAuthProviderInner
      isLoaded={isLoaded}
      isSignedIn={isSignedIn}
      getToken={getToken}
      signOut={signOut}
    >
      {children}
    </ClerkAuthProviderInner>
  );
}
